// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Dialog.Modal' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Dialog from '@wandb/weave/components/Dialog';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, useCallback, useContext, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Button as LegacyButton,
  Dropdown,
  DropdownItemProps,
  Input,
  Modal,
} from 'semantic-ui-react';

import {envIsPublicCloud} from '../../config';
import {
  useArtifactCollectionHasProtectedArtifactQuery,
  useArtifactCollectionWithRegistryVersionQuery,
  useArtifactTypesQuery,
  useDeleteArtifactPortfolioMutation,
  useDeleteArtifactSequenceMutation,
} from '../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../services/analytics';
import {
  nonDeletableArtifactTypes,
  validateArtifactName,
} from '../../util/artifacts';
import {propagateErrorsContext} from '../../util/errors';
import {useStatsigGateSourceArtifactProtection} from '../../util/rampFeatureFlags';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../Banner';
import {RegistriesSearchContext} from '../Registries/common/RegistriesSearchContextProvider';
import {checkRegistryProject} from '../Registries/common/utils';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../Registries/graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../Registries/RegistriesHomepage/hooks';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';

interface RenameArtifactModalProps {
  name: string;
  modalOpen: boolean;
  onRenameArtifact(newName: string): void;
  onClose(): void;
}

export const RenameArtifactModal = (props: RenameArtifactModalProps) => {
  const {name, modalOpen, onRenameArtifact, onClose} = props;
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState('');

  // The value for the input is set to `newName` which is a state variable.
  // Without this useEffect, any changes in props.name will not be propagated to the input value.
  React.useEffect(() => {
    setNewName(name);
  }, [name]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (!validateArtifactName(value)) {
      // Keep this expression in sync with the regex in validateArtifactName()
      value = e.target.value.replace(/[^\-.\w\s]/g, '');
    }
    setNewName(value);
    setError('');
  };

  const handleRename = () => {
    if (newName === name || newName === '') {
      setError('New name must be different and non-empty.');
    } else {
      onRenameArtifact(newName);
    }
  };

  return (
    <Modal size="tiny" open={modalOpen} onClose={onClose}>
      <Modal.Header>
        Are you sure you want to rename this artifact?
      </Modal.Header>
      <Modal.Content>
        <p>
          This will break all aliases associated with the current name. You will
          need to update your scripts to refer to the updated name.
        </p>
        <Input
          fluid
          value={newName}
          placeholder={'Artifact name...'}
          onChange={handleNameChange}
          error={error !== ''}
        />
        {error && <div style={{color: 'red', marginTop: '5px'}}>{error}</div>}
        <div style={{marginTop: '10px', fontSize: '12px', color: 'gray'}}>
          Artifact name may not contain colons, forward slashes, or backslashes.
        </div>
      </Modal.Content>
      <Modal.Actions>
        <LegacyButton onClick={onClose}>Cancel</LegacyButton>
        <LegacyButton
          color="green"
          disabled={newName === name || newName === ''}
          onClick={handleRename}>
          Rename
        </LegacyButton>
      </Modal.Actions>
    </Modal>
  );
};

interface MoveArtifactModalProps {
  entityName: string;
  projectName: string;
  modalOpen: boolean;
  onMoveArtifact(destinationArtifactTypeName: string): void;
  onClose(): void;
}

export const MoveArtifactModal = (props: MoveArtifactModalProps) => {
  const {entityName, projectName, modalOpen, onMoveArtifact, onClose} = props;
  const [value, setValue] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<DropdownItemProps[]>([]);
  const {loading} = useArtifactTypesQuery({
    fetchPolicy: 'no-cache',
    skip: !modalOpen,
    onCompleted: data => {
      setOptions(
        data!.project!.artifactTypes.edges.map(edge => {
          return {
            key: edge.node!.name,
            value: edge.node!.name,
            text: edge.node!.name,
          };
        })
      );
    },
    variables: {
      entityName,
      projectName,
    },
  });

  return (
    <Modal size="tiny" open={modalOpen} onClose={onClose}>
      <Modal.Header>Are you sure you want to move this artifact?</Modal.Header>
      <Modal.Content>
        <p>
          You will need to update your scripts to log new versions using the
          updated type name.
        </p>
        <Dropdown
          fluid
          search
          selection
          allowAdditions
          placeholder={'Destination artifact type...'}
          options={options}
          loading={loading}
          value={value}
          onChange={(e, data) => {
            setValue(data.value as string);
          }}
          onAddItem={(e, data) => {
            setOptions([
              {
                key: data.value as string,
                value: data.value as string,
                text: data.value as string,
              },
              ...options,
            ]);
          }}
        />
      </Modal.Content>
      <Modal.Actions>
        <LegacyButton onClick={onClose}>Cancel</LegacyButton>
        <LegacyButton
          color="green"
          disabled={value === undefined || value === ''}
          onClick={() => onMoveArtifact(value!)}>
          Move
        </LegacyButton>
      </Modal.Actions>
    </Modal>
  );
};

interface DeleteArtifactModalProps {
  id: string;
  entityName: string;
  isModalOpen: boolean;
  isDeletable?: boolean;
  isPortfolio?: boolean;
  newURL?: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  refetchProjectArtifacts: () => void;
  setIsDeleteModalOpen(isModalOpen: boolean): void;
}

export const DeleteArtifactModal: FC<DeleteArtifactModalProps> = ({
  id,
  entityName,
  isModalOpen,
  isDeletable = true,
  isPortfolio,
  projectName,
  artifactTypeName,
  artifactCollectionName,
  refetchProjectArtifacts,
  setIsDeleteModalOpen,
  newURL,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const history = useHistory();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {isRegistryProject} = checkRegistryProject(projectName);
  const refetchOrgRegistries = orgName != null && isRegistryProject;
  const {data: isSourceArtifactProtectionEnabled} =
    useStatsigGateSourceArtifactProtection(orgName);

  const {data: hasProtectedArtifactData, loading: hasProtectedArtifactLoading} =
    useArtifactCollectionHasProtectedArtifactQuery({
      variables: {
        artifactCollectionID: id,
      },
      skip: !isModalOpen || isPortfolio || !isSourceArtifactProtectionEnabled,
      context: propagateErrorsContext(),
    });
  const hasProtectedArtifact = useMemo(() => {
    const hasProtectedArtifact =
      hasProtectedArtifactData?.artifactSequence?.hasProtectedArtifact ?? false;
    if (hasProtectedArtifact) {
      Analytics.track('source_artifact_protection', {
        location: 'DeleteArtifactModal',
        entityName,
        projectName,
        artifactCollectionName,
      });
    }
    return hasProtectedArtifact;
  }, [
    hasProtectedArtifactData,
    entityName,
    projectName,
    artifactCollectionName,
  ]);
  const [deleteArtifactSequence] = useDeleteArtifactSequenceMutation({
    onCompleted: refetchProjectArtifacts,
    context: propagateErrorsContext(),
  });
  const {searchPattern} = useContext(RegistriesSearchContext);
  const [deleteArtifactPortfolio] = useDeleteArtifactPortfolioMutation({
    onCompleted: refetchProjectArtifacts,
    context: propagateErrorsContext(),
    refetchQueries: refetchOrgRegistries
      ? [
          {
            query: FETCH_REGISTRY_PROJECTS_PARTIAL,
            variables: {
              orgName,
              limit: FETCH_REGISTRIES_PAGE_SIZE,
              pattern: searchPattern,
            },
          },
        ]
      : [],
  });

  const {data: collectionData, loading: collectionDataLoading} =
    useArtifactCollectionWithRegistryVersionQuery({
      variables: {
        entityName,
        projectName,
        artifactTypeName,
        artifactCollectionName,
      },
      context: propagateErrorsContext(),
      skip: isPortfolio || artifactCollectionName === '' || !isModalOpen,
    });

  const closeModal = useCallback(
    () => setIsDeleteModalOpen(false),
    [setIsDeleteModalOpen]
  );

  const onDeleteArtifact = useCallback(async () => {
    setIsDeleting(true);

    try {
      if (isPortfolio) {
        await deleteArtifactPortfolio({
          variables: {
            artifactPortfolioID: id ?? '',
          },
        });
      } else {
        await deleteArtifactSequence({
          variables: {
            artifactSequenceID: id ?? '',
          },
        });
      }

      if (newURL != null) {
        history.push(newURL);
      }
      closeModal();
      toast(
        `${
          isPortfolio ? 'Collection' : 'Artifact'
        } ${artifactCollectionName} deleted.`
      );
    } catch (error) {
      console.error(error);
      toast('Something went wrong while trying to delete this artifact.');
    }

    setIsDeleting(false);
  }, [
    closeModal,
    deleteArtifactPortfolio,
    deleteArtifactSequence,
    history,
    id,
    isPortfolio,
    newURL,
    artifactCollectionName,
  ]);

  if (!isDeletable) {
    return (
      <Dialog.Root open={isModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            className="w-[600px] rounded-lg"
            onInteractOutside={e => {
              e.preventDefault();
            }}>
            <Dialog.Title>Cannot delete this artifact</Dialog.Title>
            <Dialog.Description className="pt-16" data-test="delete-summary">
              {nonDeletableArtifactTypes[artifactTypeName]} artifacts cannot be
              deleted.
            </Dialog.Description>
            <div className="mb-8 mt-24 flex items-center">
              <Dialog.Close className="ml-8" asChild>
                <Button
                  className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                  size="large">
                  Cancel
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={isModalOpen} onOpenChange={setIsDeleteModalOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[600px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>
            Delete this {isPortfolio ? 'collection' : 'artifact'}?
          </Dialog.Title>
          <Dialog.Description>
            {hasProtectedArtifact && !isPortfolio && (
              <Banner variant="error" className="mt-16">
                <BannerFlexWrapper>
                  <div>
                    <Icon name="warning" />
                  </div>
                  <BannerTextWrapper>
                    You are unable to delete this artifact until all its
                    versions have been unlinked from protected registry
                    collections.
                  </BannerTextWrapper>
                </BannerFlexWrapper>
              </Banner>
            )}
          </Dialog.Description>
          <Dialog.Description className="pt-16" data-test="delete-summary">
            {isPortfolio ? 'Collection' : 'Artifact'} "{artifactCollectionName}"
            and all its versions will be scheduled for deletion. Any references
            to these versions will be broken. Registry content linked to this
            artifact will no longer exist.
          </Dialog.Description>
          <div className="mb-8 mt-24 flex items-center">
            <Button
              data-cy="delete-artifact-sequence-confirm"
              variant="destructive"
              disabled={
                isDeleting ||
                collectionDataLoading ||
                hasProtectedArtifactLoading ||
                hasProtectedArtifact
              }
              size="large"
              onClick={onDeleteArtifact}>
              Delete
            </Button>
            <Dialog.Close className="ml-8" asChild>
              <Button
                className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                variant="ghost"
                size="large"
                disabled={isDeleting}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
